import { DeepPartial } from "react-hook-form";
import { TenantMailSendingApi } from "src/api/generated/common/mail/tenantMailSendingApi.ts";
import { useOAuthAuthentication } from "src/utils/oAuth.ts";
import i18n from "i18next";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { emailRule, requireRule } from "src/components/common/forms/validation.ts";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";
import { UserSettingsApi } from "src/api/generated/common/api/userSettingsApi.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm";
import { AppUserSettings } from "src/api/generated/erp/db/types/tables/appUserSettings";
import { EmailTemplateSelectButton } from "src/components/views/email/EmailTemplateSelectButton.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { FormFromMailField } from "src/components/views/tenantMail/FormFromMailField.tsx";
import { useRecentFromMailboxes } from "src/components/views/tenantMail/useRecentFromMailboxes.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SendEmailFormProps extends FormCommonProps<void> {
	defaultValues?: DeepPartial<SendEmailFormValues>;
}

export interface SendEmailFormValues {
	mailFrom: string;
	receiverEmail: string;
	subject: string;
	body: string;
}

export const SendEmailForm = ({ defaultValues, onCompleted, onFormEdited }: SendEmailFormProps) => {
	const { checkAuthentication } = useOAuthAuthentication(TenantMailSendingApi.ensureMailSendingAuthentication);
	const { supportsCustomFromAddress } = useGlobalInitData();
	const { currentUserEmail, recentFromMailboxes, defaultFromMailbox, rememberRecentMailbox, removeRecentMailbox } =
		useRecentFromMailboxes();

	const submitForm = async (values: SendEmailFormValues) => {
		const authResult = await checkAuthentication();
		if (authResult === "requiresAuthentication") {
			return "interrupted";
		}

		await TenantMailSendingApi.sendMailAsCurrentUser({
			mailFrom: supportsCustomFromAddress ? values.mailFrom || undefined : undefined,
			receiver: values.receiverEmail,
			subject: values.subject,
			body: values.body,
		});
		if (supportsCustomFromAddress) {
			rememberRecentMailbox(values.mailFrom);
		}
	};

	return (
		<AsyncForm<AppUserSettings, SendEmailFormValues, void>
			fetch={UserSettingsApi.getAppUserSettings}
			getDefaultValues={(data) => ({
				...defaultValues,
				mailFrom: defaultValues?.mailFrom ?? defaultFromMailbox,
				body: defaultValues?.body + "\n\n" + data.emailSignature,
			})}
			submit={submitForm}
			submitLabel={i18n.t("send")}
			fitContent={true}
			columns={1}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("email.sendEmail.form"),
				title: i18n.t("email"),
				pathSegments: [i18n.t("form")],
				data: {},
			}}
			render={({ control, setValue, data: { emailSignature } }) => (
				<>
					{supportsCustomFromAddress && (
						<FormFromMailField
							control={control}
							name={"mailFrom"}
							rules={emailRule()}
							currentUserEmail={currentUserEmail}
							recentMailboxes={recentFromMailboxes}
							onDeleteRecentMailbox={removeRecentMailbox}
						/>
					)}
					<FormTextField
						control={control}
						name={"receiverEmail"}
						rules={{
							...requireRule(),
							...emailRule(),
						}}
						tooltip={i18n.t("you_can_use_semicolon_to_separate_multiple_email_addresses")}
						label={i18n.t("receiver_mail")}
					/>
					<HorizontalBox sx={{ gap: 1, alignSelf: "stretch" }}>
						<FormTextField
							control={control}
							name={"subject"}
							rules={requireRule()}
							label={i18n.t("title")}
							sx={{ flex: 1 }}
							autoFocus
						/>
						<EmailTemplateSelectButton
							onTemplateSelected={({ subject, body }) => {
								if (subject.trim() !== "") setValue("subject", subject);
								setValue("body", body + (emailSignature ? `\n\n${emailSignature}` : ""));
							}}
						/>
					</HorizontalBox>
					<FormRichTextEditor
						control={control}
						name={"body"}
						sx={{
							flex: 1,
							gridColumn: "1 / -1",
						}}
					/>
				</>
			)}
		/>
	);
};
